/**
 * Formatting for the interface.
 *
 * Dates are shown in the operator's own timezone, because the question being
 * asked is almost always "how long ago", and the answer has to match the clock
 * on the wall. The absolute value goes in a tooltip so nothing is lost.
 */

const DATE_TIME = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const RELATIVE = new Intl.RelativeTimeFormat('ru-RU', { numeric: 'auto' });

const UNITS: readonly [Intl.RelativeTimeFormatUnit, number][] = [
  ['second', 1000],
  ['minute', 60_000],
  ['hour', 3_600_000],
  ['day', 86_400_000],
];

export function formatDateTime(value: string | Date | null): string {
  if (value === null) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? '—' : DATE_TIME.format(date);
}

/** "5 минут назад". Falls back to an absolute date past a week, where "восемь
 *  дней назад" stops being easier to read than the date itself. */
export function formatRelative(value: string | Date | null, now: Date = new Date()): string {
  if (value === null) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';

  const elapsed = date.getTime() - now.getTime();
  const magnitude = Math.abs(elapsed);

  if (magnitude > 7 * 86_400_000) return DATE_TIME.format(date);
  if (magnitude < 45_000) return 'только что';

  let chosen: [Intl.RelativeTimeFormatUnit, number] = UNITS[0]!;
  for (const unit of UNITS) {
    if (magnitude >= unit[1]) chosen = unit;
  }

  return RELATIVE.format(Math.round(elapsed / chosen[1]), chosen[0]);
}

export function formatGb(value: number): string {
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} ГБ`;
}

export function formatPercent(part: number, whole: number): string {
  if (whole <= 0) return '—';
  return `${Math.round((part / whole) * 100)}%`;
}

/** First letter of a login, for the avatar. Works on Cyrillic too. */
export function initial(value: string): string {
  return [...value.trim()][0]?.toUpperCase() ?? '?';
}

const BYTE_UNITS = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];

/**
 * A size in the largest unit that keeps it readable. The API sends backup
 * sizes as strings: the column is a bigint and JSON has no such thing.
 */
export function formatBytes(bytes: string | number | null): string {
  if (bytes === null) return '—';

  const value = Number(bytes);
  if (!Number.isFinite(value)) return '—';

  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < BYTE_UNITS.length - 1) {
    size /= 1024;
    unit += 1;
  }

  return `${size.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} ${BYTE_UNITS[unit]}`;
}

/** A count with its noun in the right Russian form: 1 сайт, 3 сайта, 5 сайтов. */
export function plural(count: number, one: string, few: string, many: string): string {
  const tens = Math.abs(count) % 100;
  const units = tens % 10;
  const word = tens > 10 && tens < 20 ? many : units === 1 ? one : units >= 2 && units <= 4 ? few : many;
  return `${count} ${word}`;
}
